import React, { useCallback, useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";
import PageHeader from "../../../../../atoms/PageHeader";
import Search from "../../../../../molecules/search";
import CustomButton from "../../../../../atoms/CustomButton";
import CustomDatagrid from "../../../../../molecules/CustomDatagrid";
import Pagination from "../../../../../molecules/Pagination";
import { setLoading } from "../../../../../../store/slices/loaderSlice";
import Modal from "../../../../../organisms/modals/Modal";
import DeleteModal from "../../../../../organisms/modals/DeleteModal";
import Form from "./Form";
import { getData } from "../../../../../../services";
import { render } from "@testing-library/react";
import Edit from "../../../../../icons/Edit";
import Delete from "../../../../../icons/Delete";
import { useDelete } from "../../../../../../hooks/useDelete";
import NoContentCard from "../../../../../atoms/NoContentCard";
import NoContentIcon from "../../../../../icons/NocontentIcon";
import { useNavigate } from "react-router-dom";
import { getRolesList } from "../../../../../../services/basicGets";

const UserManagement = ({ is_create, is_delete, is_edit }) => {
  const [cookies] = useCookies(["t"]);
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [searchedValue, setSearchedValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [modalState, setModalState] = useState({
    isModalOpen: false,
    modalData: null,
    isDeleteModalOpen: false,
    modalId: "",
    name: "",
  });

  const getUserData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getData({
        endpoint: "AdminUserManagement/getAllUsers",
        params: {
          search_query: searchedValue,
          page: currentPage,
          page_limit: itemsPerPage,
        },
        token: cookies.t,
      });

      if (res) {
        setTableData(res);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    }
  }, [searchedValue, cookies.t, currentPage, itemsPerPage]);

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  const columns = [
    {
      field: "name",
      headerName: "User Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: true,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      sortable: true,
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <>
            {params?.row.is_active ? (
              <span className="text-success fw-bold">Active</span>
            ) : (
              <span className="text-error fw-bold">Inactive</span>
            )}
          </>
        );
      },
    },
  ];

  if (is_delete || is_edit) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        return (
          <div className="table-icons-container">
            {is_edit && (
              <div onClick={() => handleEdit(params.row)}>
                <Edit />
              </div>
            )}
            {is_delete && (
              <div onClick={() => handleDeleteModal(params.row)}>
                <Delete />
              </div>
            )}
          </div>
        );
      },
    });
  }

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((perPage) => {
    setCurrentPage(1);
    setItemsPerPage(perPage);
  }, []);

  // add , edit , delete
  const openModal = (title, data) => {
    const modalId = uuidv4();
    setModalState((prevState) => ({
      ...prevState,
      modalId,
      isModalOpen: true,
      title,
      modalData: data,
    }));
  };

  const closeModal = () => {
    setModalState((prevState) => ({ ...prevState, isModalOpen: false }));
  };

  const handleEdit = (data) => {
    setModalState((prevState) => ({
      ...prevState,
      modalId: data.id,
      modalData: data,
      isModalOpen: true,
      title: "Edit User",
    }));
  };

  const deleteUser = useDelete();

  const handleDeleteModal = (data) => {
    setModalState((prevState) => ({
      ...prevState,
      isDeleteModalOpen: true,
      modalId: data.id,
      name: data.name,
    }));
  };

  const handleDeletion = async () => {
    try {
      await deleteUser("AdminUserManagement/deleteUser", "User", {
        id: modalState.modalId,
      });
      getUserData();
      setModalState((prevState) => ({
        ...prevState,
        isDeleteModalOpen: false,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const CustomNoRowsOverlay = React.memo(() => {
    return (
      <>
        {!!searchedValue ? (
          <NoContentCard
            title="No Record Found"
            type="Company"
            icon={<NoContentIcon />}
            small={true}
          />
        ) : (
          <NoContentCard
            title="No User Added Yet!"
            subtitle="Click on Add User button to add a user"
            tag="Add User"
            handleClick={() => openModal("Add User")}
            type="Company"
            icon={<NoContentIcon />}
            small={true}
          />
        )}
      </>
    );
  });

  return (
    <>
      <div className="d-flex space-between">
        <PageHeader title="User Management" type="small" />
        <div className="d-flex gap-3">
          <Search
            onSearchEmit={(value) => {
              setSearchedValue(value);
              if (value.length > 2) {
                if (currentPage !== 1) setCurrentPage(1);
              }
            }}
          />

          {is_create && (
            <CustomButton
              type="btn-primary"
              text="Add User"
              handleClick={() => openModal("Add User")}
            />
          )}
        </div>
      </div>

      <CustomDatagrid
        getRowId={(row) => row.id}
        rows={tableData?.data || []}
        columns={columns}
        tHeight="50vh"
        CustomNoRowsOverlay={CustomNoRowsOverlay}
      />
      {tableData?.total_record > 25 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={tableData?.total_record}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {modalState?.isModalOpen && (
        <Modal
          id={modalState.modalId}
          isActive={modalState.isModalOpen}
          onClose={closeModal}
          title={modalState.title}
          extrastyle={{
            overflow: "visible",
            cssText: "overflow: visible !important",
          }}
          width="600px"
        >
          <Form
            onClose={closeModal}
            id={modalState.modalId}
            initialValues={
              modalState.title === "Edit User" ? modalState.modalData : null
            }
            refetchData={getUserData}
          />
        </Modal>
      )}

      {modalState.isDeleteModalOpen && (
        <DeleteModal
          name={modalState.name}
          id={modalState.modalId}
          isActive={modalState.isDeleteModalOpen}
          onClose={() =>
            setModalState((prevState) => ({
              ...prevState,
              isDeleteModalOpen: false,
            }))
          }
          title="User"
          onClick={handleDeletion}
        />
      )}
    </>
  );
};

export default UserManagement;
